import {
  all,
  call,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects';

import { post, putRequest } from '@dmi/shared/utils/request';

import { parseReasonCodes } from '../Shared/helpers';
import {
  changeStep,
  registerGuidedStepFourFailure,
  registerGuidedStepFourSuccess,
  registerGuidedStepFourValidationFailure,
  registerGuidedStepThreeFailure,
  registerGuidedStepThreeSuccess,
  registerResendEmailFailure,
  registerResendEmailSuccess,
  registerResendEmailValidationFailure,
  registerStepOneFailure,
  registerStepOneSuccess,
  registerStepTwoFailure,
  registerStepTwoSuccess,
  registerStepTwoValidationFailure,
  registerVerifyFailure,
  registerVerifySuccess,
  resetInputs,
  verifyRecaptchaFailure,
  verifyRecaptchaSuccess,
} from './actions';
import {
  DEFAULT_REGISTER_ERRORS,
  REGISTER_GUIDED_STEP_FOUR,
  REGISTER_GUIDED_STEP_THREE,
  REGISTER_RESEND_EMAIL,
  REGISTER_SERVICE_ERROR,
  REGISTER_STEP_ONE,
  REGISTER_STEP_ONE_ERROR,
  REGISTER_STEP_ONE_SSO_ERROR,
  REGISTER_STEP_TWO,
  REGISTER_STEP_TWO_ERROR,
  REGISTER_USERNAME_ERROR,
  REGISTER_VERIFY,
  VERIFY_RECAPTCHA,
} from './constants';
import { parseRecaptchaError } from './helpers';
import makeSelectRegister from './selectors';

export function* registerStepOneSaga({ baseUrl }, { payload }) {
  try {
    const endpoint = `${baseUrl}/register/stepone`;
    const { error, registrationToken: token } = yield call(post, endpoint, payload);
    if (error) {
      const { defaultErrorsRegisterStepOne } = DEFAULT_REGISTER_ERRORS;
      const { reasons } = error;
      if (reasons[0].startsWith('3006')) {
        const {
          serviceError,
          ...validationErrors
        } = parseReasonCodes(reasons, defaultErrorsRegisterStepOne);
        if (serviceError) {
          yield put(registerStepOneFailure({
            key: 'serviceError',
            message: REGISTER_SERVICE_ERROR,
          }));
          yield put(changeStep(8));
        } else {
          const { bannerError, ssoLoanError } = validationErrors;
          yield put(resetInputs());
          if (bannerError.length) {
            yield put(registerStepOneFailure({
              key: 'bannerError',
              message: bannerError[0],
            }));
          } else if (ssoLoanError) {
            yield put(registerStepOneFailure({
              key: 'ssoLoanError',
              message: REGISTER_STEP_ONE_SSO_ERROR,
            }));
          } else {
            yield put(registerStepOneFailure({
              key: 'stepOneError',
              message: REGISTER_STEP_ONE_ERROR,
            }));
          }
          yield put(changeStep(8));
        }
      } else {
        yield put(registerStepOneFailure({
          key: 'serviceError',
          message: REGISTER_SERVICE_ERROR,
        }));
        yield put(changeStep(8));
      }
    } else {
      yield put(registerStepOneSuccess({ token }));
      yield put(changeStep(5));
    }
  } catch (error) {
    yield put(registerStepOneFailure({
      key: 'serviceError',
      message: REGISTER_SERVICE_ERROR,
    }));
    yield put(changeStep(8));
  }
}

export function* registerStepTwoSaga({ baseUrl }, { payload }) {
  try {
    const registrationToken = yield select(makeSelectRegister('token'));
    const requestBody = { ...payload, registrationToken };
    const endpoint = `${baseUrl}/register/steptwo`;
    const { error } = yield call(post, endpoint, requestBody, { sendDeviceTokens: true });
    if (error) {
      const { defaultErrorsRegisterStepTwo } = DEFAULT_REGISTER_ERRORS;
      const { reasons } = error;
      if (reasons[0].startsWith('3006')) {
        const {
          serviceError,
          ...validationErrors
        } = parseReasonCodes(reasons, defaultErrorsRegisterStepTwo);
        if (serviceError) {
          if (reasons.includes('3006.2014')) {
            yield put(registerStepTwoFailure({
              key: 'serviceErrorEmailInUse',
              message: 'Email already in use',
            }));
          } else {
            yield put(registerStepTwoFailure({
              key: 'serviceError',
              message: REGISTER_SERVICE_ERROR,
            }));
          }
          yield put(changeStep(8));
        } else {
          const { bannerError, ...fieldErrors } = validationErrors;
          if (reasons.includes('3006.2018')) { // check for 'username is already in use' error
            yield put(registerStepTwoValidationFailure(fieldErrors));
            yield put(registerStepTwoFailure({
              key: 'stepTwoError',
              message: REGISTER_USERNAME_ERROR,
            }));
          } else if (bannerError.length) {
            yield put(resetInputs());
            yield put(registerStepTwoFailure({
              key: 'bannerError',
              message: bannerError[0],
            }));
          } else {
            yield put(resetInputs());
            yield put(registerStepTwoFailure({
              key: 'stepTwoError',
              message: REGISTER_STEP_TWO_ERROR,
            }));
          }
          yield put(changeStep(8));
        }
      } else {
        yield put(registerStepTwoFailure({
          key: 'serviceError',
          message: REGISTER_SERVICE_ERROR,
        }));
        yield put(changeStep(8));
      }
    } else {
      yield put(registerStepTwoSuccess());
      yield put(changeStep(8));
    }
  } catch (error) {
    yield put(registerStepTwoFailure({
      key: 'serviceError',
      message: REGISTER_SERVICE_ERROR,
    }));
    yield put(changeStep(8));
  }
}

/**
 * @description Makes API call for Register Resend request
 *
 * @return void
 */
export function* registerResendSaga({ baseUrl }, { payload }) {
  try {
    const endpoint = `${baseUrl}/register/resend`;
    const { error } = yield call(putRequest, endpoint, payload);
    if (error) {
      const { defaultErrorsRegisterResend } = DEFAULT_REGISTER_ERRORS;
      const { reasons } = error;
      if (reasons[0].startsWith('3006')) {
        const {
          serviceError,
          ...validationErrors
        } = parseReasonCodes(reasons, defaultErrorsRegisterResend);
        if (serviceError) {
          yield put(registerResendEmailFailure({ message: REGISTER_SERVICE_ERROR }));
          yield put(changeStep(8));
        } else {
          yield put(registerResendEmailValidationFailure(validationErrors));
        }
      } else {
        yield put(registerResendEmailFailure({ message: REGISTER_SERVICE_ERROR }));
        yield put(changeStep(8));
      }
    } else {
      yield put(registerResendEmailSuccess());
      yield put(changeStep(8));
    }
  } catch (error) {
    yield put(registerResendEmailFailure({ message: REGISTER_SERVICE_ERROR }));
    yield put(changeStep(8));
  }
}

/**
 * @description Makes API call for Guided Registration Step Four request
 *
 * @return void
 */
export function* registerGuidedStepFourSaga({ baseUrl }, { payload }) {
  try {
    const { password, username } = payload;
    const registrationToken = yield select(makeSelectRegister('token'));
    const endpoint = `${baseUrl}/register/guided/stepfour`;
    const { error } = yield call(post, endpoint, { password, registrationToken, username });
    if (error) {
      const { defaultErrorsRegisterGuidedStepFour } = DEFAULT_REGISTER_ERRORS;
      const { reasons } = error;
      if (reasons[0].startsWith('3006')) {
        const {
          serviceError,
          ...validationErrors
        } = parseReasonCodes(reasons, defaultErrorsRegisterGuidedStepFour);
        if (serviceError) {
          yield put(registerGuidedStepFourFailure({
            key: 'serviceError',
            message: REGISTER_SERVICE_ERROR,
          }));
          yield put(changeStep(2));
        } else {
          const { bannerError, ...fieldErrors } = validationErrors;
          if (bannerError.length) {
            yield put(registerGuidedStepFourFailure({
              key: 'bannerError',
              message: bannerError[0],
            }));
            yield put(changeStep(2));
          } else if (Object.values(fieldErrors).some((val) => !!val)) {
            yield put(registerGuidedStepFourValidationFailure(fieldErrors));
          } else {
            yield put(registerGuidedStepFourFailure({
              key: 'serviceError',
              message: REGISTER_SERVICE_ERROR,
            }));
            yield put(changeStep(2));
          }
        }
      } else {
        yield put(registerGuidedStepFourFailure({
          key: 'serviceError',
          message: REGISTER_SERVICE_ERROR,
        }));
        yield put(changeStep(2));
      }
    } else {
      yield put(registerGuidedStepFourSuccess());
      yield put(changeStep(2));
    }
  } catch (error) {
    yield put(registerGuidedStepFourFailure({
      key: 'serviceError',
      message: REGISTER_SERVICE_ERROR,
    }));
    yield put(changeStep(2));
  }
}

/**
 * @description Makes API call for Guided Registration Step Three request
 *
 * @return void
 */
export function* registerGuidedStepThreeSaga({ baseUrl }, { payload }) {
  try {
    const endpoint = `${baseUrl}/register/guided/stepthree`;
    const { error, registrationToken: token } = yield call(putRequest, endpoint, payload);
    if (error) {
      throw new Error('serviceError');
    } else {
      yield put(registerGuidedStepThreeSuccess(token));
    }
  } catch (error) {
    yield put(registerGuidedStepThreeFailure({
      key: 'serviceError',
      message: REGISTER_SERVICE_ERROR,
    }));
    yield put(changeStep(2));
  }
}

export function* registerVerifySaga({ baseUrl }, { payload }) {
  try {
    const endpoint = `${baseUrl}/register/verify`;
    const { error } = yield call(putRequest, endpoint, payload, { sendDeviceTokens: true });
    if (error) {
      const [reason] = error.reasons;
      if (reason.startsWith('3006')) {
        // Checks if reason is an errorCode or reasonCode
        const isReasonCode = reason.includes('.');
        if (!isReasonCode) {
          // loan is already registered/verified. Navigate to success view
          yield put(registerVerifySuccess());
        } else {
          yield put(registerVerifyFailure(REGISTER_SERVICE_ERROR));
        }
      } else {
        yield put(registerVerifyFailure(REGISTER_SERVICE_ERROR));
      }
    } else {
      yield put(registerVerifySuccess());
    }
  } catch {
    yield put(registerVerifyFailure(REGISTER_SERVICE_ERROR));
  }
}

/**
 * @description Verifies recaptcha response using Google's API
 *
 * @return void
 */
export function* verifyRecaptchaSaga({ baseUrl }, { payload }) {
  const { resetRecaptcha, response } = payload;
  try {
    const endpoint = `${baseUrl}/recaptcha`;
    const { error } = yield call(post, endpoint, { response });
    if (error) {
      const { reasons } = error;
      const isServiceError = parseRecaptchaError(reasons);
      yield call(resetRecaptcha);
      yield put(verifyRecaptchaFailure());
      if (isServiceError) {
        yield put(registerStepOneFailure({
          key: 'serviceError',
          message: REGISTER_SERVICE_ERROR,
        }));
        yield put(changeStep(8));
      }
    } else {
      yield put(verifyRecaptchaSuccess());
    }
  } catch (error) {
    yield call(resetRecaptcha);
    yield put(verifyRecaptchaFailure());
  }
}

export default (options) => function* rootSaga() {
  yield all([
    takeLatest(REGISTER_GUIDED_STEP_FOUR, registerGuidedStepFourSaga, options),
    takeLatest(REGISTER_GUIDED_STEP_THREE, registerGuidedStepThreeSaga, options),
    takeLatest(REGISTER_RESEND_EMAIL, registerResendSaga, options),
    takeLatest(REGISTER_STEP_ONE, registerStepOneSaga, options),
    takeLatest(REGISTER_STEP_TWO, registerStepTwoSaga, options),
    takeLatest(REGISTER_VERIFY, registerVerifySaga, options),
    takeLatest(VERIFY_RECAPTCHA, verifyRecaptchaSaga, options),
  ]);
};
